import React, { useMemo, useState } from 'react';
import { SearchType } from 'utils/types';
import { useNavigate } from 'react-router-dom';
import { ROUTE_KEYS, ROUTE_PREFIX } from 'utils/constants';
import DeleteModal, { DeleteModalType } from 'components/base/delete-modal';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import useDashboardContext from 'contexts/dashboard.context';
import { ENUM_BRAND_TYPE, ENUM_CATEGORY } from 'utils/enum';
import BrandCard from 'components/brand-card';
import { BrandCardOptionType } from 'components/brand-card/brand-card-options';

type BrandSearchProps = {
  search: SearchType;
  searchMeal: () => void;
  selectedResults: SearchType[];
  setSelectedResults: (value: SearchType[]) => void;
  onMoveBrand: (type: ENUM_BRAND_TYPE) => void;
  singleMealClick: (brand: SearchType) => void;
};
const BrandSearchReults = ({
  search,
  searchMeal,
  selectedResults,
  setSelectedResults,
  onMoveBrand,
  singleMealClick
}: BrandSearchProps) => {
  const { category } = useDashboardContext();
  const navigate = useNavigate();
  const [showArchiveBrandModal, setShowArchiveBrandModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [type, setType] = useState<DeleteModalType>('archive');

  const selectedResultIds = useMemo(() => selectedResults.map(({ id }) => id), [selectedResults]);

  const archiveBrand = async () => {
    setShowArchiveBrandModal(!showArchiveBrandModal);
  };

  const handleArchive = async () => {
    try {
      setDeleteLoading(true);

      const payload = {
        brand_id: search?.id,
        archived: type === 'archive'
      };
      const res = await apiService.post(`brand_management/brand/archived`, { data: payload });

      if (res?.data) {
        toast(
          <ToastMessage
            type='success'
            message={`${search?.name} ${type === 'archive' ? 'archived' : 'restored'} successfully`}
          />
        );
        setDeleteLoading(false);
        setShowArchiveBrandModal(false);
        searchMeal();
      }
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err.response.data.message} />);
    } finally {
      setDeleteLoading(false);
    }
  };

  const onEditClick = (brand_id: string) => {
    const keyword = category === ENUM_CATEGORY.FOOD ? 'brand' : 'shop';
    navigate(
      `${ROUTE_PREFIX.FOOD}/${category}/${ROUTE_KEYS.OVERVIEW}/${keyword}/${brand_id}/details?edit=true`
    );
  };
  const handleChange = (e: any, searchItem: SearchType) => {
    if (selectedResultIds.includes(searchItem.id)) {
      setSelectedResults(selectedResults.filter((item) => item.id !== searchItem.id));
    } else {
      setSelectedResults([...selectedResults, searchItem]);
    }
  };
  const handleBrandOption = (option: BrandCardOptionType, item: SearchType) => {
    switch (option) {
      case 'archive':
        setType('archive');
        archiveBrand();
        break;

      case 'edit':
        onEditClick(item.id);
        break;

      case 'restore':
        setType('restore');
        archiveBrand();
        break;

      default:
        break;
    }
  };

  return (
    <div>
      <BrandCard
        data={search}
        options={['restore', 'edit', 'archive']}
        isChecked={selectedResultIds.includes(search.id)}
        onCheck={(e) => handleChange(e, search)}
        onClick={(option) => handleBrandOption(option, search)}
        onMove={(type) => {
          singleMealClick(search);
          onMoveBrand(type);
        }}
      />
      {showArchiveBrandModal && (
        <DeleteModal
          onClose={archiveBrand}
          show
          description={`Are you sure you want to ${
            type === 'archive' ? 'archive' : 'restore'
          } archive ${search?.name}`}
          handleDelete={handleArchive}
          loading={deleteLoading}
          title={`${type === 'archive' ? 'Archive' : 'Restore'}  ${search?.name}?`}
          type={type}
        />
      )}
    </div>
  );
};

export default BrandSearchReults;
